import { Badge, Box, ButtonDropdown, ColumnLayout, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import './AccountingCalendar.scss';
import React from 'react';
import CustomTag from 'src/models/accounting-calendar/CustomTag';
import DateUtils from 'src/utils/dateUtils';

interface Props {
    tag: CustomTag;
    onEdit: () => void;
    onDelete: () => void;
    onClose: () => void;
    /* Whether to show the kebab (Edit/Delete) menu. Callers that only view a tag's
       details — e.g. the dashboard Open Tasks list, which has no edit/delete flow —
       pass false. Defaults to true so the accounting-calendar view is unchanged. */
    showActions?: boolean;
}

/**
 * Display an accounting-date offset without the redundant plus sign: "WD+2" -> "WD 2".
 * Negative offsets ("WD-3") and calendar dates (e.g. "2026-07-01") are shown unchanged.
 */
function formatDateValue(value?: string | null): string | null | undefined {
    if (!value) {
        return value;
    }
    return value.replace(/^WD\+(\d+)$/, 'WD $1');
}

function ValueRow(props: { label: string; value?: string | number | null }) {
    if (props.value === undefined || props.value === null || props.value === '') {
        return null;
    }
    return (
        <div>
            <Box variant="awsui-key-label">{props.label}</Box>
            <div>{props.value}</div>
        </div>
    );
}

/* Like ValueRow but always renders a cell (showing a dash for empty values) so the
   enclosing ColumnLayout keeps a fixed column count instead of collapsing. */
function FixedValueRow(props: { label: string; value?: string | number | null }) {
    const empty = props.value === undefined || props.value === null || props.value === '';
    return (
        <div>
            <Box variant="awsui-key-label">{props.label}</Box>
            <div>{empty ? '—' : props.value}</div>
        </div>
    );
}

export default function TagDetailsModal(props: Props) {
    const { tag, onEdit, onDelete, onClose, showActions = true } = props;
    return (
        <Modal
            data-testid="tagDetailsModal"
            visible={true}
            size="medium"
            onDismiss={onClose}
            header={
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <span className={tag.isRecurring ? '' : 'acctCalendarBadgeOneTime'}>
                        <Badge color={tag.isRecurring ? 'blue' : 'grey'}>
                            {tag.isRecurring ? 'Recurring' : 'One-time'}
                        </Badge>
                    </span>
                    <span>{tag.tagEventName}</span>
                </SpaceBetween>
            }
        >
            {/* Kebab menu anchored directly under the modal's close (X) button. */}
            {showActions && (
                <div className="tagActionsMenu">
                    <ButtonDropdown
                        variant="icon"
                        ariaLabel="Tag actions"
                        items={[
                            { id: 'edit', text: 'Edit' },
                            { id: 'delete', text: 'Delete' },
                        ]}
                        onItemClick={({ detail }) => {
                            if (detail.id === 'edit') {
                                onEdit();
                            } else if (detail.id === 'delete') {
                                onDelete();
                            }
                        }}
                    />
                </div>
            )}
            <SpaceBetween direction="vertical" size="m">
                {/* Description shown full-width at the top, matching mockup. */}
                <ValueRow label="Description" value={tag.description || '_'} />

                {/* Row 1: Recipients | Date | Type. Uses a plain CSS grid (not Cloudscape
                    ColumnLayout) because ColumnLayout collapses columns={3} to 2 below the
                    688px (xs) container breakpoint, which a medium modal never reaches. */}
                <div className="acctTagDetailGrid">
                    <FixedValueRow label="Recipients" value={tag.recipients} />
                    <FixedValueRow label="Date" value={formatDateValue(tag.dateValue)} />
                    <FixedValueRow label="Type" value={tag.isRecurring ? 'Recurring' : 'One-time'} />
                </div>

                {/* Row 2: Frequency | Recurrence start | Recurrence end — recurring tags only. */}
                {tag.isRecurring && (
                    <div className="acctTagDetailGrid">
                        <FixedValueRow label="Frequency" value={tag.recurringFrequency} />
                        <FixedValueRow label="Recurrence start" value={tag.recurrenceStartDate} />
                        <FixedValueRow label="Recurrence end" value={tag.recurrenceEndDate || 'Open-ended'} />
                    </div>
                )}

                {/* Excluded months — shown only when the tag actually has excluded months. */}
                {tag.isRecurring && tag.excludedMonths && tag.excludedMonths.length > 0 && (
                    <ValueRow label="Excluded months" value={tag.excludedMonths.join(', ')} />
                )}

                {/* Audit fields: server-generated metadata, visually de-emphasized below a
                    divider so it reads as secondary to the user-entered tag details. */}
                <div className="acctTagAuditSection">
                    <ColumnLayout columns={2} variant="text-grid">
                        <ValueRow label="Created at" value={tag.createdAt ? DateUtils.formatTimestamp(tag.createdAt) : undefined} />
                        <ValueRow label="Created by" value={tag.createdBy} />
                        <ValueRow label="Last updated at" value={tag.lastUpdatedAt ? DateUtils.formatTimestamp(tag.lastUpdatedAt) : undefined} />
                        <ValueRow label="Last updated by" value={tag.lastUpdatedBy} />
                    </ColumnLayout>
                </div>
            </SpaceBetween>
        </Modal>
    );
}
